import React, { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, AlertCircle, Lock } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { GoogleGIcon } from "../components/GoogleGIcon";
import { useAuthStore } from "../state/authStore";
import { SEO } from "../components/SEO";

export const Login = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/dashboard";
    const reset = params.get("reset");
    const error = params.get("error");
    const { login } = useAuthStore();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [status, setStatus] = useState("idle");
    const [errMsg, setErrMsg] = useState(
        error === "google_canceled"
            ? "Google sign-in was canceled. Try again or use your password."
            : error === "google_failed"
                ? "Google sign-in failed. Please try again."
                : ""
    );

    // Phase 7b.2 — direct Google OAuth. Full-page redirect to the backend
    // login starter; `next` is forwarded so the post-auth landing matches the
    // page the user was originally trying to reach.
    //
    // Use the current origin (NOT REACT_APP_BACKEND_URL) so the redirect works
    // unchanged across preview, production, and any custom domain — both the
    // frontend and the backend are served behind the same ingress, with /api/*
    // routed to FastAPI.
    const startGoogle = () => {
        const back = window.location.origin;
        const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : "/dashboard";
        window.location.href = `${back}/api/auth/google/login?next=${encodeURIComponent(safeNext)}`;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (status === "loading") return;
        setStatus("loading");
        setErrMsg("");
        try {
            await login(email.trim(), password);
            navigate(decodeURIComponent(next));
        } catch (err) {
            setStatus("idle");
            setErrMsg(err.message || "Sign-in failed.");
        }
    };

    return (
        <div className="pt-24 md:pt-28 pb-12 mx-auto max-w-6xl px-5 md:px-10" data-testid="login-page">
            <SEO noindex title="Sign in" canonicalPath="/login" />
            <div className="grid lg:grid-cols-12 gap-10 items-center">
                {/* Left hero copy */}
                <Reveal className="lg:col-span-5">
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                        Sign in
                    </p>
                    <h1
                        className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05]"
                        data-testid="login-title"
                    >
                        Welcome back to <span className="text-accent-cyan">Forefront</span>.
                    </h1>
                    <p className="mt-5 text-text-muted leading-relaxed max-w-md">
                        Your portal for managed firewall provisioning, SOC operations, and the
                        ZeroConf wizard.
                    </p>
                </Reveal>

                {/* Right card */}
                <Reveal className="lg:col-span-7" delay={0.1}>
                    <div className="rounded-2xl border border-white/[0.06] bg-surface/70 backdrop-blur-md p-6 md:p-8 max-w-md ml-auto">
                        {reset === "success" && (
                            <div
                                className="mb-4 text-[12.5px] text-accent-mint bg-accent-mint/5 border border-accent-mint/25 rounded-md px-3 py-2"
                                data-testid="login-reset-success"
                            >
                                Password updated. Please sign in.
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={startGoogle}
                            className="w-full inline-flex items-center justify-center gap-2.5 py-2.5 rounded-md border border-accent-cyan/40 bg-accent-cyan/[0.06] text-[14px] font-medium text-text-primary hover:bg-accent-cyan/[0.12] hover:shadow-glow-cyan transition-all duration-200"
                            data-testid="login-google-btn"
                        >
                            <GoogleGIcon className="w-4 h-4" />
                            Continue with Google
                        </button>

                        <div className="flex items-center gap-3 my-5">
                            <span className="flex-1 h-px bg-white/[0.06]" />
                            <span className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                                or
                            </span>
                            <span className="flex-1 h-px bg-white/[0.06]" />
                        </div>

                        <form onSubmit={submit} className="space-y-3" data-testid="login-form">
                            <input
                                type="email"
                                required
                                placeholder="Work email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                data-testid="login-email"
                                autoComplete="email"
                            />
                            <div className="relative">
                                <input
                                    type={showPw ? "text" : "password"}
                                    required
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 pr-9 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                    data-testid="login-password"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-dim hover:text-text-muted transition-colors duration-150"
                                    aria-label={showPw ? "Hide password" : "Show password"}
                                    data-testid="login-toggle-pw"
                                    tabIndex={-1}
                                >
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            <div className="flex items-center justify-end">
                                <Link
                                    to="/forgot-password"
                                    className="text-[12px] text-text-muted hover:text-accent-cyan transition-colors duration-150"
                                    data-testid="login-forgot-link"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                            {errMsg && (
                                <div
                                    className="flex items-center gap-2 text-[12px] text-accent-red bg-accent-red/5 border border-accent-red/20 rounded-md px-3 py-2"
                                    data-testid="login-error"
                                >
                                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                    {errMsg}
                                </div>
                            )}
                            <MagneticButton
                                type="submit"
                                disabled={status === "loading"}
                                className="w-full py-2.5 rounded-md bg-accent-cyan text-canvas text-[13.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-40 transition-shadow duration-200"
                                data-testid="login-submit"
                                ariaLabel="Sign in"
                            >
                                <span className="inline-flex items-center justify-center gap-2">
                                    <Lock className="w-3.5 h-3.5" />
                                    {status === "loading" ? "Signing in…" : "Sign in"}
                                </span>
                            </MagneticButton>
                        </form>

                        <p className="mt-5 text-center text-[12.5px] text-text-muted">
                            Don&apos;t have an account?{" "}
                            <Link
                                to={`/signup${next ? `?next=${encodeURIComponent(next)}` : ""}`}
                                className="text-accent-cyan hover:underline"
                                data-testid="login-signup-link"
                            >
                                Sign up →
                            </Link>
                        </p>
                    </div>
                </Reveal>
            </div>
        </div>
    );
};

export default Login;
